import * as rq from '@tanstack/react-query';

import { useDexFetchClient } from '@/api/looker/utils/use-dex-fetch-client';
import { ChartType } from '@/data-explorer-v2/entities/chart-type';
import type { Node } from '@/data-explorer-v2/entities/node';
import { useMe } from '@/hooks/use-me';

// Reverse mapping from API category to ChartType
const chartTypeFromCategory: Record<string, ChartType> = {
  cell_voltage_chart: ChartType.CELL_VOLTAGE,
  current_ac_chart: ChartType.CURRENT_AC,
  current_chart: ChartType.CURRENT,
  energy_chart: ChartType.ENERGY,
  frequency_chart: ChartType.FREQUENCY,
  hs_control_agc_chart: ChartType.HS_CONTROL_AGC,
  power_chart: ChartType.POWER,
  power_factor_chart: ChartType.POWER_FACTOR,
  racks_online_chart: ChartType.RACKS_ONLINE,
  soc_chart: ChartType.SOC,
  temperature_chart: ChartType.TEMPERATURE,
  units_online_chart: ChartType.UNITS_ONLINE,
  voltage_ac_chart: ChartType.VOLTAGE_AC,
  voltage_chart: ChartType.VOLTAGE,
};

export namespace GetAvailableChartTypes {
  export const key = (node: Node) => [
    'data-explorer',
    'available-chart-types',
    node.id,
    node.level,
    node.source,
  ];

  export type QueryParams = {
    enabled?: boolean;
    retry?: number | boolean;
  };

  export type Input = Node;
  export type Output = ChartType[];

  type FetchColumnsResult = Record<string, string[]>;

  export function useQuery(
    node: Input | undefined,
    opts?: QueryParams,
  ): rq.UseQueryResult<Output> {
    const fetchClient = useDexFetchClient();
    const me = useMe();

    return rq.useQuery({
      enabled: Boolean(node) && (opts?.enabled ?? true),
      retry: opts?.retry,
      queryKey: node ? key(node) : ['data-explorer', 'available-chart-types'],
      async queryFn({ signal }) {
        if (!node) {
          throw new Error('Node is required');
        }

        const columnsResult = await fetchClient<FetchColumnsResult>({
          path: `/v2/columns/${me.tenant}/${node.level}/${node.id}`,
          params: {
            ...(node.source !== '' && {
              'data-source': node.source,
            }),
          },
          signal,
        });

        // Return empty array if no columns available
        if (!columnsResult || Object.keys(columnsResult).length === 0) {
          return [];
        }

        // Extract available chart types from the columns response
        const availableChartTypes: ChartType[] = [];

        for (const category of Object.keys(columnsResult)) {
          const chartType = chartTypeFromCategory[category];
          if (chartType) {
            availableChartTypes.push(chartType);
          }
        }

        return availableChartTypes;
      },
    });
  }
}
